import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import BusinessQRCode from '@/components/BusinessQRCode';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ArrowLeft, Building, User, Mail, MapPin, Briefcase, 
  Phone, Edit, Save, X, Facebook, Instagram, Linkedin, Youtube, AtSign, Globe, Lock, Shield
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COUNTRY_CODES = [
  { code: '+1', country: 'US', flag: '🇺🇸' },
  { code: '+44', country: 'UK', flag: '🇬🇧' },
  { code: '+91', country: 'IN', flag: '🇮🇳' },
  { code: '+86', country: 'CN', flag: '🇨🇳' },
  { code: '+81', country: 'JP', flag: '🇯🇵' },
  { code: '+82', country: 'KR', flag: '🇰🇷' },
  { code: '+49', country: 'DE', flag: '🇩🇪' },
  { code: '+33', country: 'FR', flag: '🇫🇷' },
  { code: '+39', country: 'IT', flag: '🇮🇹' },
  { code: '+34', country: 'ES', flag: '🇪🇸' },
  { code: '+61', country: 'AU', flag: '🇦🇺' },
  { code: '+55', country: 'BR', flag: '🇧🇷' },
  { code: '+52', country: 'MX', flag: '🇲🇽' },
  { code: '+7', country: 'RU', flag: '🇷🇺' },
  { code: '+27', country: 'ZA', flag: '🇿🇦' }
];

export default function BusinessProfile() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editForm, setEditForm] = useState({
    street_address: '',
    city: '',
    state: '',
    country: 'United States',
    phone_country_code: '+1',
    phone_number: '',
    support_email: '',
    website_url: '',
    facebook_url: '',
    instagram_url: '',
    linkedin_url: '',
    youtube_url: '',
    email_signature: ''
  });

  // Passcode-related state
  const [showPasscodeDialog, setShowPasscodeDialog] = useState(false);
  const [passcodeDialogType, setPasscodeDialogType] = useState('');
  const [passcodeForm, setPasscodeForm] = useState({
    password: '',
    passcode: '',
    confirmPasscode: ''
  });
  const [processingPasscode, setProcessingPasscode] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile(response.data);
      
      // Initialize edit form with existing data
      const phone = response.data.phone_number || '';
      let countryCode = '+1';
      let phoneNum = phone;
      
      // Extract country code if phone exists
      if (phone) {
        const match = phone.match(/^(\+\d+)/);
        if (match) {
          countryCode = match[1];
          phoneNum = phone.replace(countryCode, '').trim();
        }
      }
      
      // Parse address if it exists (backwards compatibility)
      const address = response.data.business_address || '';
      let street = '', city = '', state = '';
      if (address) {
        const parts = address.split(',').map(p => p.trim());
        street = parts[0] || '';
        city = parts[1] || '';
        state = parts[2] || '';
      }
      
      setEditForm({
        street_address: response.data.street_address || street,
        city: response.data.city || city,
        state: response.data.state || state,
        country: response.data.country || 'United States',
        phone_country_code: countryCode,
        phone_number: phoneNum,
        support_email: response.data.support_email || '',
        website_url: response.data.website_url || '',
        facebook_url: response.data.facebook_url || '',
        instagram_url: response.data.instagram_url || '',
        linkedin_url: response.data.linkedin_url || '',
        youtube_url: response.data.youtube_url || '',
        email_signature: response.data.email_signature || ''
      });
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Failed to load profile');
    }
  };

  const handleEdit = () => {
    // Auto-generate email signature if empty
    if (!profile.email_signature || profile.email_signature.trim() === '') {
      const autoSignature = generateDefaultSignature();
      setEditForm(prev => ({
        ...prev,
        email_signature: autoSignature
      }));
    }
    
    // Check if user has a login passcode set
    if (profile.login_passcode) {
      setPasscodeDialogType('verify');
      setPasscodeForm({ password: '', passcode: '', confirmPasscode: '' });
      setShowPasscodeDialog(true);
    } else {
      setIsEditing(true);
    }
  };

  // Helper function to generate default email signature
  const generateDefaultSignature = () => {
    const lines = ['Best regards,'];
    
    if (profile.business_name) {
      lines.push(profile.business_name);
    }
    
    if (profile.owner_name) {
      lines.push(profile.owner_name);
    }
    
    if (profile.phone_number) {
      lines.push(`Phone: ${profile.phone_number}`);
    }
    
    if (profile.email) {
      lines.push(`Email: ${profile.email}`);
    }
    
    if (profile.website_url) {
      const cleanUrl = profile.website_url.replace('https://', '').replace('http://', '');
      lines.push(`Visit us at ${cleanUrl}`);
    }
    
    return lines.join('\n');
  };

  const handleCancel = () => {
    setIsEditing(false);
    fetchProfile();
  };

  // Helper function to ensure URL has protocol
  const ensureHttpProtocol = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    return `https://${url}`;
  };

  // Login Passcode handlers (used for both profile editing and login)
  const handleSetLoginPasscode = () => {
    setPasscodeDialogType('set-login');
    setPasscodeForm({ password: '', passcode: '', confirmPasscode: '' });
    setShowPasscodeDialog(true);
  };

  const handleChangeLoginPasscode = () => {
    setPasscodeDialogType('change-login');
    setPasscodeForm({ password: '', passcode: '', confirmPasscode: '' });
    setShowPasscodeDialog(true);
  };

  const handleRemoveLoginPasscode = () => {
    setPasscodeDialogType('remove-login');
    setPasscodeForm({ password: '', passcode: '', confirmPasscode: '' });
    setShowPasscodeDialog(true);
  };

  // Password change handler
  const handleChangePassword = () => {
    setPasscodeDialogType('change-password');
    setPasscodeForm({ password: '', passcode: '', confirmPasscode: '' });
    setShowPasscodeDialog(true);
  };

  const handlePasscodeSubmit = async () => {
    const token = localStorage.getItem('token');
    
    if (passcodeDialogType === 'verify') {
      // Verify login passcode for profile editing
      if (!passcodeForm.passcode || passcodeForm.passcode.length !== 6) {
        toast.error('Please enter your 6-digit login passcode');
        return;
      }
      
      setProcessingPasscode(true);
      try {
        // Verify by attempting login with passcode
        const response = await axios.post(`${API}/auth/login`, {
          email: profile.email,
          password: passcodeForm.passcode,
          role: 'business'
        });
        
        // Auto-generate email signature if empty
        if (!profile.email_signature || profile.email_signature.trim() === '') {
          const autoSignature = generateDefaultSignature();
          setEditForm(prev => ({
            ...prev,
            email_signature: autoSignature
          }));
        }
        
        setShowPasscodeDialog(false);
        setIsEditing(true);
        toast.success('Passcode verified');
      } catch (error) {
        toast.error('Invalid passcode');
      } finally {
        setProcessingPasscode(false);
      }
    } else if (passcodeDialogType === 'set-login' || passcodeDialogType === 'change-login') {
      // Set or change login passcode
      if (!passcodeForm.password) {
        toast.error('Please enter your account password');
        return;
      }
      if (!passcodeForm.passcode || passcodeForm.passcode.length !== 6) {
        toast.error('Login passcode must be exactly 6 digits');
        return;
      }
      if (!/^\d+$/.test(passcodeForm.passcode)) {
        toast.error('Login passcode must contain only numbers');
        return;
      }
      if (passcodeForm.passcode !== passcodeForm.confirmPasscode) {
        toast.error('Passcodes do not match');
        return;
      }
      
      setProcessingPasscode(true);
      try {
        await axios.post(`${API}/auth/set-login-passcode`, 
          { password: passcodeForm.password, passcode: passcodeForm.passcode },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        await fetchProfile();
        setShowPasscodeDialog(false);
        toast.success(passcodeDialogType === 'set-login' ? 'Login passcode set successfully' : 'Login passcode changed successfully');
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to set login passcode');
      } finally {
        setProcessingPasscode(false);
      }
    } else if (passcodeDialogType === 'remove-login') {
      // Remove login passcode
      if (!passcodeForm.password) {
        toast.error('Please enter your account password');
        return;
      }
      
      setProcessingPasscode(true);
      try {
        await axios.delete(`${API}/auth/remove-login-passcode?password=${encodeURIComponent(passcodeForm.password)}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        await fetchProfile();
        setShowPasscodeDialog(false);
        toast.success('Login passcode removed successfully');
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to remove login passcode');
      } finally {
        setProcessingPasscode(false);
      }
    } else if (passcodeDialogType === 'change-password') {
      // Change password using either current password OR passcode
      const hasPasscode = passcodeForm.passcode && passcodeForm.passcode.length === 6;
      const hasPassword = passcodeForm.password;
      
      if (!hasPasscode && !hasPassword) {
        toast.error('Please enter either your current password or 6-digit passcode');
        return;
      }
      
      if (!passcodeForm.confirmPasscode || passcodeForm.confirmPasscode.length < 6) {
        toast.error('New password must be at least 6 characters');
        return;
      }
      
      setProcessingPasscode(true);
      try {
        // Verify credentials first
        const verifyPayload = {
          email: profile.email,
          password: hasPasscode ? passcodeForm.passcode : passcodeForm.password,
          role: 'business'
        };
        
        await axios.post(`${API}/auth/login`, verifyPayload);
        
        // Now update password
        await axios.post(`${API}/auth/change-password`, 
          { 
            current_credential: hasPasscode ? passcodeForm.passcode : passcodeForm.password,
            new_password: passcodeForm.confirmPasscode,
            credential_type: hasPasscode ? 'passcode' : 'password'
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        setShowPasscodeDialog(false);
        toast.success('Password changed successfully');
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to change password');
      } finally {
        setProcessingPasscode(false);
      }
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      // Combine country code and phone number
      const fullPhone = editForm.phone_number 
        ? `${editForm.phone_country_code}${editForm.phone_number}`
        : '';
      
      const updateData = {
        street_address: editForm.street_address,
        city: editForm.city,
        state: editForm.state,
        country: editForm.country,
        phone_number: fullPhone,
        support_email: editForm.support_email,
        website_url: editForm.website_url,
        facebook_url: editForm.facebook_url,
        instagram_url: editForm.instagram_url,
        linkedin_url: editForm.linkedin_url,
        youtube_url: editForm.youtube_url,
        email_signature: editForm.email_signature
      };
      
      await axios.put(`${API}/business/profile`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Profile updated successfully!');
      setIsEditing(false);
      fetchProfile();
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  if (!profile) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <p className="font-outfit text-gray-600">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream pt-6 pb-12" data-testid="business-profile-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Button
          onClick={() => navigate('/business/dashboard')}
          variant="ghost"
          className="mb-6"
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        <div className="flex justify-between items-center mb-8">
          <h1 className="font-playfair font-bold text-4xl text-primary">Business Profile</h1>
          {!isEditing ? (
            <Button onClick={handleEdit} className="bg-primary hover:bg-primary/90">
              <Edit className="w-4 h-4 mr-2" />
              Edit Profile
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button onClick={handleCancel} variant="outline" disabled={saving}>
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <Button onClick={handleSave} className="bg-green-600 hover:bg-green-700" disabled={saving}>
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          )}
        </div>

        <Card className="p-8">
          {/* Header */}
          <div className="flex items-center mb-8">
            <div className="w-20 h-20 bg-gradient-to-br from-amber-600 to-amber-500 rounded-full flex items-center justify-center mr-6">
              <Building className="w-10 h-10 text-white" />
            </div>
            <div>
              <h2 className="font-playfair font-bold text-3xl text-primary mb-1">{profile.business_name}</h2>
              <p className="font-outfit text-sm text-gray-500" data-testid="business-id">ID: {profile.business_id}</p>
            </div>
          </div>

          {/* Basic Info (Read-only) */}
          <div className="space-y-4 mb-6">
            <div className="flex items-start">
              <User className="w-5 h-5 text-amber-600 mr-4 mt-1" />
              <div>
                <p className="font-outfit text-sm text-gray-500">Owner Name</p>
                <p className="font-outfit text-lg text-gray-800" data-testid="owner-name">{profile.owner_name}</p>
              </div>
            </div>

            <div className="flex items-start">
              <Mail className="w-5 h-5 text-amber-600 mr-4 mt-1" />
              <div>
                <p className="font-outfit text-sm text-gray-500">Email</p>
                <p className="font-outfit text-lg text-gray-800" data-testid="email">{profile.email}</p>
              </div>
            </div>

            <div className="flex items-start">
              <Briefcase className="w-5 h-5 text-amber-600 mr-4 mt-1" />
              <div>
                <p className="font-outfit text-sm text-gray-500">Business Type</p>
                <p className="font-outfit text-lg text-gray-800" data-testid="business-type">{profile.business_type}</p>
              </div>
            </div>
          </div>

          {/* Editable Fields */}
          <div className="border-t pt-6">
            <h3 className="font-playfair font-bold text-xl text-primary mb-4">Contact & Social Information</h3>
            
            {isEditing ? (
              <div className="space-y-6">
                {/* Address */}
                <div className="space-y-4">
                  <Label className="flex items-center gap-2 mb-2">
                    <MapPin className="w-4 h-4 text-amber-600" />
                    Business Address
                  </Label>
                  
                  <div>
                    <Label htmlFor="street-address" className="text-sm text-gray-600">Street Address</Label>
                    <Input
                      id="street-address"
                      value={editForm.street_address}
                      onChange={(e) => setEditForm({ ...editForm, street_address: e.target.value })}
                      placeholder="123 Main Street"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="city" className="text-sm text-gray-600">City</Label>
                      <Input
                        id="city"
                        value={editForm.city}
                        onChange={(e) => setEditForm({ ...editForm, city: e.target.value })}
                        placeholder="City"
                      />
                    </div>
                    <div>
                      <Label htmlFor="state" className="text-sm text-gray-600">State</Label>
                      <Input
                        id="state"
                        value={editForm.state}
                        onChange={(e) => setEditForm({ ...editForm, state: e.target.value })}
                        placeholder="State"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="country" className="text-sm text-gray-600">Country</Label>
                    <Input
                      id="country"
                      value={editForm.country}
                      onChange={(e) => setEditForm({ ...editForm, country: e.target.value })}
                      placeholder="Country"
                    />
                  </div>
                </div>

                {/* Phone Number */}
                <div>
                  <Label htmlFor="phone-number" className="flex items-center gap-2 mb-2">
                    <Phone className="w-4 h-4 text-amber-600" />
                    Phone Number
                  </Label>
                  <div className="flex gap-2">
                    <Select
                      value={editForm.phone_country_code}
                      onValueChange={(value) => setEditForm({ ...editForm, phone_country_code: value })}
                    >
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {COUNTRY_CODES.map((country) => (
                          <SelectItem key={country.code} value={country.code}>
                            {country.flag} {country.code}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      id="phone-number"
                      type="tel"
                      value={editForm.phone_number}
                      onChange={(e) => setEditForm({ ...editForm, phone_number: e.target.value })}
                      placeholder="1234567890"
                      className="flex-1"
                    />
                  </div>
                </div>

                {/* Business Email */}
                <div>
                  <Label htmlFor="support-email" className="flex items-center gap-2 mb-2">
                    <AtSign className="w-4 h-4 text-amber-600" />
                    Business Email
                  </Label>
                  <Input
                    id="support-email"
                    type="email"
                    value={editForm.support_email}
                    onChange={(e) => setEditForm({ ...editForm, support_email: e.target.value })}
                    placeholder="support@yourbusiness.com"
                  />
                </div>

                {/* Social Media Links */}
                <div className="border-t pt-6">
                  <h4 className="font-semibold text-gray-800 mb-4">Social & Online Presence</h4>
                  
                  <div className="space-y-4">
                    {/* Website */}
                    <div>
                      <Label htmlFor="website" className="flex items-center gap-2 mb-2">
                        <Globe className="w-4 h-4 text-gray-600" />
                        Website
                      </Label>
                      <Input
                        id="website"
                        value={editForm.website_url}
                        onChange={(e) => setEditForm({ ...editForm, website_url: e.target.value })}
                        placeholder="https://yourbusiness.com"
                      />
                    </div>

                    {/* Facebook */}
                    <div>
                      <Label htmlFor="facebook" className="flex items-center gap-2 mb-2">
                        <Facebook className="w-4 h-4 text-blue-600" />
                        Facebook
                      </Label>
                      <div className="flex items-center">
                        <span className="bg-gray-100 border border-r-0 border-gray-300 px-3 py-2 text-sm text-gray-600 rounded-l-md">
                          facebook.com/
                        </span>
                        <Input
                          id="facebook"
                          value={editForm.facebook_url?.replace('https://facebook.com/', '').replace('https://www.facebook.com/', '').replace('http://facebook.com/', '') || ''}
                          onChange={(e) => setEditForm({ ...editForm, facebook_url: e.target.value ? `https://facebook.com/${e.target.value}` : '' })}
                          placeholder="yourbusiness"
                          className="rounded-l-none"
                        />
                      </div>
                    </div>

                    {/* Instagram */}
                    <div>
                      <Label htmlFor="instagram" className="flex items-center gap-2 mb-2">
                        <Instagram className="w-4 h-4 text-pink-600" />
                        Instagram
                      </Label>
                      <div className="flex items-center">
                        <span className="bg-gray-100 border border-r-0 border-gray-300 px-3 py-2 text-sm text-gray-600 rounded-l-md">
                          instagram.com/
                        </span>
                        <Input
                          id="instagram"
                          value={editForm.instagram_url?.replace('https://instagram.com/', '').replace('https://www.instagram.com/', '').replace('http://instagram.com/', '') || ''}
                          onChange={(e) => setEditForm({ ...editForm, instagram_url: e.target.value ? `https://instagram.com/${e.target.value}` : '' })}
                          placeholder="yourbusiness"
                          className="rounded-l-none"
                        />
                      </div>
                    </div>

                    {/* LinkedIn */}
                    <div>
                      <Label htmlFor="linkedin" className="flex items-center gap-2 mb-2">
                        <Linkedin className="w-4 h-4 text-blue-700" />
                        LinkedIn
                      </Label>
                      <div className="flex items-center">
                        <span className="bg-gray-100 border border-r-0 border-gray-300 px-3 py-2 text-sm text-gray-600 rounded-l-md">
                          linkedin.com/company/
                        </span>
                        <Input
                          id="linkedin"
                          value={editForm.linkedin_url?.replace('https://linkedin.com/company/', '').replace('https://www.linkedin.com/company/', '').replace('http://linkedin.com/company/', '') || ''}
                          onChange={(e) => setEditForm({ ...editForm, linkedin_url: e.target.value ? `https://linkedin.com/company/${e.target.value}` : '' })}
                          placeholder="yourbusiness"
                          className="rounded-l-none"
                        />
                      </div>
                    </div>

                    {/* YouTube */}
                    <div>
                      <Label htmlFor="youtube" className="flex items-center gap-2 mb-2">
                        <Youtube className="w-4 h-4 text-red-600" />
                        YouTube
                      </Label>
                      <div className="flex items-center">
                        <span className="bg-gray-100 border border-r-0 border-gray-300 px-3 py-2 text-sm text-gray-600 rounded-l-md">
                          youtube.com/@
                        </span>
                        <Input
                          id="youtube"
                          value={editForm.youtube_url?.replace('https://youtube.com/@', '').replace('https://www.youtube.com/@', '').replace('http://youtube.com/@', '') || ''}
                          onChange={(e) => setEditForm({ ...editForm, youtube_url: e.target.value ? `https://youtube.com/@${e.target.value}` : '' })}
                          placeholder="yourbusiness"
                          className="rounded-l-none"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Email Signature */}
                <div className="border-t pt-6">
                  <Label htmlFor="email-signature" className="flex items-center gap-2 mb-2">
                    <Mail className="w-4 h-4 text-amber-600" />
                    Email Signature
                  </Label>
                  <p className="text-xs text-gray-500 mb-2">This will be used in your promotional emails</p>
                  <Textarea
                    id="email-signature"
                    value={editForm.email_signature || ''}
                    onChange={(e) => setEditForm({ ...editForm, email_signature: e.target.value })}
                    placeholder={`Best regards,\n${editForm.business_name}\n${editForm.phone_number || ''}`}
                    rows={4}
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Address Display */}
                {(profile.street_address || profile.city || profile.state || profile.country || profile.business_address) && (
                  <div className="flex items-start">
                    <MapPin className="w-5 h-5 text-amber-600 mr-4 mt-1" />
                    <div>
                      <p className="font-outfit text-sm text-gray-500">Business Address</p>
                      <p className="font-outfit text-lg text-gray-800">
                        {profile.street_address && <>{profile.street_address}<br /></>}
                        {(profile.city || profile.state) && (
                          <>
                            {profile.city}{profile.city && profile.state && ', '}{profile.state}
                            <br />
                          </>
                        )}
                        {profile.country && <>{profile.country}</>}
                        {!profile.street_address && !profile.city && !profile.state && profile.business_address && profile.business_address}
                      </p>
                    </div>
                  </div>
                )}

                {/* Phone Display */}
                {profile.phone_number && (
                  <div className="flex items-start">
                    <Phone className="w-5 h-5 text-amber-600 mr-4 mt-1" />
                    <div>
                      <p className="font-outfit text-sm text-gray-500">Phone Number</p>
                      <p className="font-outfit text-lg text-gray-800">{profile.phone_number}</p>
                    </div>
                  </div>
                )}

                {/* Business Email Display */}
                {profile.support_email && (
                  <div className="flex items-start">
                    <AtSign className="w-5 h-5 text-amber-600 mr-4 mt-1" />
                    <div>
                      <p className="font-outfit text-sm text-gray-500">Business Email</p>
                      <p className="font-outfit text-lg text-gray-800">{profile.support_email}</p>
                    </div>
                  </div>
                )}

                {/* Email Signature Display - Always show */}
                <div className="border-t pt-4 mt-4">
                  <div className="flex items-start">
                    <Mail className="w-5 h-5 text-amber-600 mr-4 mt-1" />
                    <div className="flex-1">
                      <p className="font-outfit text-sm text-gray-500 mb-2">Email Signature</p>
                      {profile.email_signature ? (
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                          <pre className="font-outfit text-sm text-gray-800 whitespace-pre-wrap">{profile.email_signature}</pre>
                        </div>
                      ) : (
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                          <p className="font-outfit text-sm text-gray-500 italic">No email signature set</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Social Media Display */}
                {(profile.website_url || profile.facebook_url || profile.instagram_url || profile.linkedin_url || profile.youtube_url) && (
                  <div className="border-t pt-4 mt-4">
                    <p className="font-outfit text-sm text-gray-500 mb-3">Social & Online Presence</p>
                    <div className="space-y-3">
                      {/* Website */}
                      {profile.website_url && (
                        <div>
                          <Label className="flex items-center gap-2 mb-2">
                            <Globe className="w-4 h-4 text-gray-600" />
                            Website
                          </Label>
                          <div className="flex items-center w-full">
                            <span className="bg-gray-100 border border-r-0 border-gray-300 px-3 py-2 text-sm text-gray-600 rounded-l-md">
                              https://
                            </span>
                            <a
                              href={ensureHttpProtocol(profile.website_url)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex-1 bg-gray-50 border border-gray-300 px-3 py-2 text-sm text-gray-800 rounded-r-md cursor-pointer hover:underline"
                            >
                              {profile.website_url?.replace('https://', '').replace('http://', '')}
                            </a>
                          </div>
                        </div>
                      )}

                      {/* Facebook */}
                      {profile.facebook_url && (
                        <div>
                          <Label className="flex items-center gap-2 mb-2">
                            <Facebook className="w-4 h-4 text-blue-600" />
                            Facebook
                          </Label>
                          <div className="flex items-center w-full">
                            <span className="bg-gray-100 border border-r-0 border-gray-300 px-3 py-2 text-sm text-gray-600 rounded-l-md">
                              facebook.com/
                            </span>
                            <a
                              href={ensureHttpProtocol(profile.facebook_url)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex-1 bg-gray-50 border border-gray-300 px-3 py-2 text-sm text-gray-800 rounded-r-md cursor-pointer hover:underline"
                            >
                              {profile.facebook_url?.replace('https://facebook.com/', '').replace('https://www.facebook.com/', '').replace('http://facebook.com/', '')}
                            </a>
                          </div>
                        </div>
                      )}

                      {/* Instagram */}
                      {profile.instagram_url && (
                        <div>
                          <Label className="flex items-center gap-2 mb-2">
                            <Instagram className="w-4 h-4 text-pink-600" />
                            Instagram
                          </Label>
                          <div className="flex items-center w-full">
                            <span className="bg-gray-100 border border-r-0 border-gray-300 px-3 py-2 text-sm text-gray-600 rounded-l-md">
                              instagram.com/
                            </span>
                            <a
                              href={ensureHttpProtocol(profile.instagram_url)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex-1 bg-gray-50 border border-gray-300 px-3 py-2 text-sm text-gray-800 rounded-r-md cursor-pointer hover:underline"
                            >
                              {profile.instagram_url?.replace('https://instagram.com/', '').replace('https://www.instagram.com/', '').replace('http://instagram.com/', '')}
                            </a>
                          </div>
                        </div>
                      )}

                      {/* LinkedIn */}
                      {profile.linkedin_url && (
                        <div>
                          <Label className="flex items-center gap-2 mb-2">
                            <Linkedin className="w-4 h-4 text-blue-700" />
                            LinkedIn
                          </Label>
                          <div className="flex items-center w-full">
                            <span className="bg-gray-100 border border-r-0 border-gray-300 px-3 py-2 text-sm text-gray-600 rounded-l-md">
                              linkedin.com/company/
                            </span>
                            <a
                              href={ensureHttpProtocol(profile.linkedin_url)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex-1 bg-gray-50 border border-gray-300 px-3 py-2 text-sm text-gray-800 rounded-r-md cursor-pointer hover:underline"
                            >
                              {profile.linkedin_url?.replace('https://linkedin.com/company/', '').replace('https://www.linkedin.com/company/', '').replace('http://linkedin.com/company/', '')}
                            </a>
                          </div>
                        </div>
                      )}

                      {/* YouTube */}
                      {profile.youtube_url && (
                        <div>
                          <Label className="flex items-center gap-2 mb-2">
                            <Youtube className="w-4 h-4 text-red-600" />
                            YouTube
                          </Label>
                          <div className="flex items-center w-full">
                            <span className="bg-gray-100 border border-r-0 border-gray-300 px-3 py-2 text-sm text-gray-600 rounded-l-md">
                              youtube.com/@
                            </span>
                            <a
                              href={ensureHttpProtocol(profile.youtube_url)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex-1 bg-gray-50 border border-gray-300 px-3 py-2 text-sm text-gray-800 rounded-r-md cursor-pointer hover:underline"
                            >
                              {profile.youtube_url?.replace('https://youtube.com/@', '').replace('https://www.youtube.com/@', '').replace('http://youtube.com/@', '')}
                            </a>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Empty State */}
                {!profile.business_address && !profile.phone_number && !profile.support_email && 
                 !profile.facebook_url && !profile.instagram_url && !profile.linkedin_url && !profile.youtube_url && (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
                    <p className="text-gray-600 mb-3">No contact or social information added yet</p>
                    <Button onClick={handleEdit} variant="outline" size="sm">
                      <Edit className="w-4 h-4 mr-2" />
                      Add Information
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="pt-4 mt-6 border-t">
            <p className="font-outfit text-xs text-gray-500">
              Member since: {new Date(profile.created_at).toLocaleDateString()}
            </p>
          </div>

          {/* Login Passcode & Security Section */}
          {!isEditing && (
            <div className="pt-4 border-t mt-4">
              <div className="flex items-start gap-3">
                <Lock className="w-5 h-5 text-primary mt-1" />
                <div className="flex-1">
                  <h3 className="font-outfit font-semibold text-gray-900 mb-2">Account Security</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    {profile.login_passcode 
                      ? 'You can log in and edit your profile using your 6-digit passcode. You can also change your password using your passcode.'
                      : 'Set up a 6-digit passcode for quick login and profile editing. You will need your account password to set it up.'
                    }
                  </p>
                  
                  <div className="space-y-3">
                    {/* Login Passcode Management */}
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-2">Login Passcode</p>
                      <div className="flex flex-wrap gap-2">
                        {!profile.login_passcode ? (
                          <Button
                            onClick={handleSetLoginPasscode}
                            size="sm"
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                          >
                            <Lock className="w-4 h-4 mr-2" />
                            Set Passcode
                          </Button>
                        ) : (
                          <>
                            <Button
                              onClick={handleChangeLoginPasscode}
                              size="sm"
                              variant="outline"
                            >
                              Change Passcode
                            </Button>
                            <Button
                              onClick={handleRemoveLoginPasscode}
                              size="sm"
                              variant="outline"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              Remove Passcode
                            </Button>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Password Management */}
                    <div className="pt-3 border-t">
                      <p className="text-sm font-medium text-gray-700 mb-2">Account Password</p>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          onClick={handleChangePassword}
                          size="sm"
                          variant="outline"
                        >
                          <Shield className="w-4 h-4 mr-2" />
                          Change Password
                        </Button>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        {profile.login_passcode 
                          ? 'Change password using your current password OR your 6-digit passcode'
                          : 'Change password using your current password'
                        }
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </Card>

        {/* Passcode Dialog */}
        <Dialog open={showPasscodeDialog} onOpenChange={setShowPasscodeDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-outfit">
                {passcodeDialogType === 'verify' && 'Enter Passcode'}
                {passcodeDialogType === 'set-login' && 'Set Login Passcode'}
                {passcodeDialogType === 'change-login' && 'Change Login Passcode'}
                {passcodeDialogType === 'remove-login' && 'Remove Login Passcode'}
                {passcodeDialogType === 'change-password' && 'Change Password'}
              </DialogTitle>
              <DialogDescription className="font-outfit">
                {passcodeDialogType === 'verify' && 'Enter your 6-digit passcode to edit your profile.'}
                {passcodeDialogType === 'set-login' && 'Create a 6-digit passcode for quick login and profile editing.'}
                {passcodeDialogType === 'change-login' && 'Enter your account password and create a new 6-digit passcode.'}
                {passcodeDialogType === 'remove-login' && 'Enter your account password to remove your login passcode.'}
                {passcodeDialogType === 'change-password' && (profile.login_passcode 
                  ? 'Enter your current password OR your 6-digit passcode, then enter your new password.'
                  : 'Enter your current password, then enter your new password.')}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              {passcodeDialogType === 'verify' ? (
                <div>
                  <Label htmlFor="passcode">Login Passcode</Label>
                  <Input
                    id="passcode"
                    type="password"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="Enter 6-digit passcode"
                    value={passcodeForm.passcode}
                    onChange={(e) => setPasscodeForm({ ...passcodeForm, passcode: e.target.value.replace(/\D/g, '') })}
                    onKeyDown={(e) => e.key === 'Enter' && handlePasscodeSubmit()}
                  />
                </div>
              ) : passcodeDialogType === 'change-password' ? (
                <>
                  {profile.login_passcode ? (
                    <>
                      <div>
                        <Label htmlFor="password">Current Password (Optional)</Label>
                        <Input
                          id="password"
                          type="password"
                          placeholder="Enter your current password"
                          value={passcodeForm.password}
                          onChange={(e) => setPasscodeForm({ ...passcodeForm, password: e.target.value })}
                        />
                      </div>
                      <div className="text-center text-sm text-gray-500">— OR —</div>
                      <div>
                        <Label htmlFor="passcode">6-Digit Passcode (Optional)</Label>
                        <Input
                          id="passcode"
                          type="password"
                          inputMode="numeric"
                          maxLength={6}
                          placeholder="Enter your 6-digit passcode"
                          value={passcodeForm.passcode}
                          onChange={(e) => setPasscodeForm({ ...passcodeForm, passcode: e.target.value.replace(/\D/g, '') })}
                        />
                      </div>
                    </>
                  ) : (
                    <div>
                      <Label htmlFor="password">Current Password</Label>
                      <Input
                        id="password"
                        type="password"
                        placeholder="Enter your current password"
                        value={passcodeForm.password}
                        onChange={(e) => setPasscodeForm({ ...passcodeForm, password: e.target.value })}
                      />
                    </div>
                  )}
                  <div>
                    <Label htmlFor="new-password">New Password</Label>
                    <Input
                      id="new-password"
                      type="password"
                      placeholder="Enter new password (min 6 characters)"
                      value={passcodeForm.confirmPasscode}
                      onChange={(e) => setPasscodeForm({ ...passcodeForm, confirmPasscode: e.target.value })}
                      onKeyDown={(e) => e.key === 'Enter' && handlePasscodeSubmit()}
                    />
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <Label htmlFor="password">Account Password</Label>
                    <Input
                      id="password"
                      type="password"
                      placeholder="Enter your account password"
                      value={passcodeForm.password}
                      onChange={(e) => setPasscodeForm({ ...passcodeForm, password: e.target.value })}
                    />
                  </div>

                  {passcodeDialogType !== 'remove-login' && (
                    <>
                      <div>
                        <Label htmlFor="new-passcode">New Login Passcode (6 digits)</Label>
                        <Input
                          id="new-passcode"
                          type="password"
                          inputMode="numeric"
                          maxLength={6}
                          placeholder="Enter 6 digit passcode"
                          value={passcodeForm.passcode}
                          onChange={(e) => setPasscodeForm({ ...passcodeForm, passcode: e.target.value.replace(/\D/g, '') })}
                        />
                      </div>

                      <div>
                        <Label htmlFor="confirm-passcode">Confirm Passcode</Label>
                        <Input
                          id="confirm-passcode"
                          type="password"
                          inputMode="numeric"
                          maxLength={6}
                          placeholder="Re-enter passcode"
                          value={passcodeForm.confirmPasscode}
                          onChange={(e) => setPasscodeForm({ ...passcodeForm, confirmPasscode: e.target.value.replace(/\D/g, '') })}
                          onKeyDown={(e) => e.key === 'Enter' && handlePasscodeSubmit()}
                        />
                      </div>
                    </>
                  )}
                </>
              )}
            </div>

            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setShowPasscodeDialog(false)}
                disabled={processingPasscode}
              >
                Cancel
              </Button>
              <Button
                onClick={handlePasscodeSubmit}
                disabled={processingPasscode}
                className="bg-primary hover:bg-primary/90"
              >
                {processingPasscode ? 'Processing...' : 'Confirm'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* QR Code Section */}
        <div className="mt-8">
          <BusinessQRCode />
        </div>
      </div>
    </div>
  );
}
